import { ParentSize } from "@visx/responsive";
import { first, isEmpty, last } from "lodash/fp";

import { Divider, Stack, Typography } from "@mui/material";

import { isFreePlan } from "@app/analytics/account/Billing/utils";
import READ_AVERAGES from "@app/analytics/constants/readAverages";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import TimeSeriesChart, {
  TimeSeriesChartContainer,
  TimeSeriesChartLabel,
  TimeSeriesChartLegend,
  TimeSeriesChartTitle,
} from "@app/analytics/MeetingReport/components/TimeSeriesChart";
import { MeetingReportTier } from "@app/analytics/MeetingReport/types";
import useAuth from "@app/common/auth/useAuth";
import ROUTES from "@app/common/routes";
import { BillingState, TimeSeriesData } from "@app/common/types";

import { Link } from "@rui/Link";
import { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";

// min width of container that the charts are still reasonable to show; any smaller and the
// charts will be so small as to be meaningless and/or the visx ScaleSVG component will get
// confused and start seizing
const MIN_CONTAINER_WITH_CHARTS_WIDTH = 410;
const CHART_RANGE: [number, number] = [50, 100];

const domainFromData = (data: TimeSeriesData[]): [number, number] => {
  if (isEmpty(data)) return [0, 0];
  return [first(data)!.x, last(data)!.x];
};

type TimeSeriesChartData = { overall: number; trend: TimeSeriesData[] };

interface MeetingReportRecapChartsProps {
  engagement: TimeSeriesChartData;
  meetingScore: TimeSeriesChartData;
  sentiment: TimeSeriesChartData;
  showCharts: boolean;
  tier: MeetingReportTier;
  tierDescription: BillingState | "unknown";
}

type MeetingReportRecapChartsContainerProps = Omit<
  MeetingReportRecapChartsProps,
  "showCharts"
>;

const QuotaExceededTooltip = ({ upsell }: { upsell: boolean }) => (
  <>
    You used your monthly allowance of premium reporting features
    {upsell && (
      <>
        . For unlimited access to metrics, transcription, playback, and more,{" "}
        <Link
          to={ROUTES.UPGRADE_ACCOUNT}
          sx={{
            textDecoration: "underline",
            fontWeight: 400,
            color: "shades.primary.300",
          }}
        >
          upgrade your account
        </Link>
        .
      </>
    )}
  </>
);

const RecordingDisabledTooltip = ({ upsell }: { upsell: boolean }) => {
  if (upsell) {
    return (
      <>
        In order to report on Read Score and Sentiment, Read needs recording
        permissions. You can{" "}
        <Link
          to={ROUTES.USER_SETTINGS_RECORDING_AND_PLAYBACK}
          sx={{
            textDecoration: "underline",
            fontWeight: 400,
            color: "shades.primary.300",
          }}
        >
          enable automatic recording
        </Link>{" "}
        in settings.
      </>
    );
  }

  return (
    <>
      Recording permissions were not enabled at the time of this meeting so Read
      Score and Sentiment were not generated
    </>
  );
};

const LiteMetricsUpsell = ({
  tierDescription,
}: {
  tierDescription: MeetingReportRecapChartsProps["tierDescription"];
}) => {
  const { user } = useAuth();
  const { currentPlan } = useBillingContext();
  const tooltipTitle =
    tierDescription === "quota_exceeded" ? (
      <QuotaExceededTooltip upsell={isFreePlan(currentPlan.type)} />
    ) : (
      <RecordingDisabledTooltip upsell={!user?.automaticRecordingEnabled} />
    );

  return (
    <>
      <Divider
        orientation="vertical"
        sx={{ color: "shades.neutral.100", height: "80%" }}
      />
      <Row
        variant="center"
        sx={{ bgcolor: "shades.ghost.300", borderRadius: "2px", p: 1 }}
        spacing={1}
      >
        <Typography variant="labelReg" sx={{ color: "shades.neutral.400" }}>
          Advanced metrics not available
        </Typography>
        <TooltipIcon placement="bottom" title={tooltipTitle} />
      </Row>
    </>
  );
};

const RecapChart = ({
  average,
  data,
  showChart,
  title,
}: {
  average: number | null;
  data: TimeSeriesChartData;
  showChart: boolean;
  title: string;
}) => (
  <Stack flexGrow={1} sx={{ minWidth: 0, overflow: "hidden" }}>
    <TimeSeriesChartTitle>{title}</TimeSeriesChartTitle>
    <TimeSeriesChartContainer variant={showChart ? "center" : "left"}>
      <TimeSeriesChartLabel score={data.overall} />
      {showChart && (
        <TimeSeriesChart
          average={average}
          data={data.trend}
          domain={domainFromData(data.trend)}
          range={CHART_RANGE}
        />
      )}
    </TimeSeriesChartContainer>
  </Stack>
);

const MeetingReportRecapCharts = ({
  engagement,
  meetingScore,
  sentiment,
  showCharts,
  tier,
  tierDescription,
}: MeetingReportRecapChartsProps) => (
  <Row
    variant="center"
    width="100%"
    maxWidth="100%"
    paddingBottom={2}
    spacing={2}
  >
    {tier !== "lite" && (
      <RecapChart
        average={READ_AVERAGES.meetingScore.value}
        data={meetingScore}
        showChart={showCharts}
        title="Read Score"
      />
    )}
    <RecapChart
      average={READ_AVERAGES.engagement.value}
      data={engagement}
      showChart={showCharts}
      title="Engagement"
    />
    {tier !== "lite" && (
      <RecapChart
        average={READ_AVERAGES.sentiment.value}
        data={sentiment}
        showChart={showCharts}
        title="Sentiment"
      />
    )}
    {showCharts && <TimeSeriesChartLegend />}
    {tier === "lite" && <LiteMetricsUpsell tierDescription={tierDescription} />}
  </Row>
);

const MeetingReportRecapChartsContainer = (
  props: MeetingReportRecapChartsContainerProps
) => (
  <ParentSize>
    {({ width }) => (
      <MeetingReportRecapCharts
        {...props}
        showCharts={width > MIN_CONTAINER_WITH_CHARTS_WIDTH}
      />
    )}
  </ParentSize>
);

export default MeetingReportRecapChartsContainer;
